import type { AuthResponse, MeResponse } from './types';
import { isMiniApp, TG_REFRESH_KEY, tgStorage } from './telegram';

/**
 * HTTP-клиент панели.
 *
 * Четыре решения, каждое — заплатка на конкретный дефект предшественника:
 *
 *   1. Access-токен живёт В ПАМЯТИ. В localStorage его утащит первый же XSS,
 *      а вкладка и так переживает перезагрузку за счёт refresh-токена.
 *   2. Refresh single-flight. Открытая панель шлёт с десяток запросов разом;
 *      без общего промиса каждый 401 порождал бы свой POST /auth/refresh, а
 *      ротация отозвала бы токен у остальных — и оператора выкидывало бы на
 *      логин ровно в момент всплеска нагрузки.
 *   3. Повтор ровно ОДИН раз. Второй 401 после свежего токена — это уже отказ
 *      в правах, а не протухший токен; цикл здесь означал бы бесконечный шторм.
 *   4. Единая форма ошибки. Бэкенд отдаёт { statusCode, message, error,
 *      requestId }, message бывает массивом (zod) — разбирать это в каждом
 *      компоненте невозможно.
 */

// ──────────────────────────── Базовые адреса ────────────────────────────

/**
 * Пустой VITE_API_BASE — относительный /api: тот же origin, dev-прокси Vite,
 * cookie считается same-site. Абсолютный адрес нужен только Mini App.
 */
export const API_BASE = (import.meta.env.VITE_API_BASE ?? '/api').replace(/\/+$/, '');

/** Origin для socket.io: тот же хост, но без пути /api. */
export function apiOrigin(): string {
  if (import.meta.env.VITE_SOCKET_URL) return import.meta.env.VITE_SOCKET_URL;
  if (!API_BASE.startsWith('http')) return window.location.origin;
  return new URL(API_BASE).origin;
}

/** Абсолютный URL ресурса API — например, для <img src> подписанного медиа. */
export function apiUrl(path: string): string {
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return API_BASE.startsWith('http')
    ? `${API_BASE}${suffix}`
    : `${window.location.origin}${API_BASE}${suffix}`;
}

// ──────────────────────────── Ошибки ────────────────────────────

/** Форма ошибки бэкенда: apps/api/src/common/filters/all-exceptions.filter.ts. */
interface ErrorResponseBody {
  statusCode?: number;
  message?: string | string[];
  error?: string;
  requestId?: string;
}

/**
 * Единая ошибка клиента.
 *
 * `code` — машинное имя причины ('Unauthorized', 'Not Found', 'network'),
 * `message` — человеческий текст, уже пригодный для показа оператору.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | null;
  /** Полный список сообщений валидации: message склеен из них для показа. */
  readonly details: string[];

  constructor(init: {
    status: number;
    message: string;
    code: string;
    requestId?: string | null;
    details?: string[];
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.requestId = init.requestId ?? null;
    this.details = init.details ?? [];
  }

  /** Сеть недоступна: показывать «сервер отверг запрос» здесь неправильно. */
  get isNetwork(): boolean {
    return this.status === 0;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }
}

/** Сузить неизвестную ошибку до ApiError — для обработчиков в компонентах. */
export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/** Текст ошибки для оператора. Годится и для не-ApiError. */
export function errorMessage(error: unknown, fallback = 'Что-то пошло не так'): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

const HTTP_REASONS: Record<number, string> = {
  0: 'Нет связи с сервером',
  400: 'Некорректный запрос',
  401: 'Требуется вход',
  403: 'Недостаточно прав',
  404: 'Не найдено',
  409: 'Конфликт данных',
  413: 'Файл слишком большой',
  422: 'Некорректные данные',
  429: 'Слишком много запросов, подождите',
  500: 'Внутренняя ошибка сервера',
  502: 'Сервер недоступен',
  503: 'Сервер недоступен',
  504: 'Сервер не ответил вовремя',
};

async function toApiError(response: Response): Promise<ApiError> {
  let body: ErrorResponseBody | null = null;
  try {
    body = (await response.json()) as ErrorResponseBody;
  } catch {
    // Не-JSON в теле (прокси отдал html, обрыв) — форму собираем по статусу.
  }

  const details = Array.isArray(body?.message)
    ? body.message
    : typeof body?.message === 'string'
      ? [body.message]
      : [];

  return new ApiError({
    status: response.status,
    message: details.join('. ') || HTTP_REASONS[response.status] || response.statusText || 'Ошибка',
    code: body?.error ?? String(response.status),
    requestId: body?.requestId ?? response.headers.get('x-request-id'),
    details,
  });
}

// ──────────────────────────── Токен в памяти ────────────────────────────

let accessToken: string | null = null;
/** Момент, после которого токен точно протух. Секунды epoch. */
let accessExpiresAt = 0;

export function getAccessToken(): string | null {
  return accessToken;
}

/**
 * Положить свежий токен. Слушателей уведомляем, потому что сокет держит копию
 * токена в handshake.auth и обязан её обновить.
 */
export function setAccessToken(token: string | null, expiresIn?: number): void {
  accessToken = token;
  accessExpiresAt = token && expiresIn ? Date.now() / 1000 + expiresIn : 0;
  for (const listener of tokenListeners) listener(token);
}

/** Токен ещё не протух по времени жизни. Запас — на дорогу до сервера. */
export function hasFreshToken(): boolean {
  return accessToken !== null && (accessExpiresAt === 0 || accessExpiresAt - 5 > Date.now() / 1000);
}

type TokenListener = (token: string | null) => void;
const tokenListeners = new Set<TokenListener>();

export function onAccessToken(listener: TokenListener): () => void {
  tokenListeners.add(listener);
  return () => tokenListeners.delete(listener);
}

/**
 * Что делать, когда сессия кончилась окончательно.
 *
 * Обработчик ставит стор авторизации: сам клиент про роутер ничего не знает, а
 * `location.assign('/login')` перезагрузил бы приложение и потерял бы
 * несохранённый черновик в композере.
 */
type UnauthorizedHandler = () => void;

/**
 * Запасной обработчик работает до того, как стор успел поставить свой (и на
 * случай, если панель поднимут без него). Он же объясняет, почему проверка
 * пути здесь есть: без неё 401 на самой странице входа перезагружал бы её по
 * кругу.
 */
let onUnauthorized: UnauthorizedHandler = () => {
  if (!window.location.pathname.startsWith('/login')) {
    window.location.assign('/login');
  }
};

export function setUnauthorizedHandler(handler: UnauthorizedHandler): void {
  onUnauthorized = handler;
}

// ──────────────────────────── Запрос ────────────────────────────

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export interface RequestOptions {
  /** Тело: объект уедет JSON'ом, FormData — как есть, с родным boundary. */
  body?: unknown;
  /** Query-параметры. undefined и null пропускаются, массив повторяет ключ. */
  query?: QueryParams;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** Не пытаться обновлять токен на 401 (сами маршруты auth). */
  skipAuthRefresh?: boolean;
}

export type QueryValue = string | number | boolean | null | undefined | readonly (string | number)[];
export type QueryParams = Record<string, QueryValue>;

/**
 * Собрать query.
 *
 * Массив повторяет ключ (`status=NEW&status=OPEN`), потому что именно так его
 * читает zod-схема фильтров на бэке; `tagIds[]` или запятая там не разберутся.
 */
export function buildQuery(params: QueryParams | undefined): string {
  if (!params) return '';
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const item of value) search.append(key, String(item));
    } else {
      search.append(key, String(value));
    }
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

function buildHeaders(options: RequestOptions, isForm: boolean): Headers {
  const headers = new Headers(options.headers);
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
  // Заголовок читает контроллер auth: он решает, куда положить refresh —
  // в httpOnly cookie (веб) или в тело ответа (Mini App).
  if (isMiniApp()) headers.set('X-Client', 'miniapp');
  // Content-Type у FormData ставит браузер: он обязан дописать boundary.
  if (!isForm && options.body !== undefined) headers.set('Content-Type', 'application/json');
  return headers;
}

async function rawRequest(
  method: Method,
  path: string,
  options: RequestOptions,
): Promise<Response> {
  const isForm = options.body instanceof FormData;
  const url = `${API_BASE}${path.startsWith('/') ? path : `/${path}`}${buildQuery(options.query)}`;

  const init: RequestInit = {
    method,
    headers: buildHeaders(options, isForm),
    // Refresh-токен веба живёт в httpOnly cookie: без credentials браузер её
    // не отправит, и обновление сессии не сработает вовсе.
    credentials: 'include',
  };
  if (options.signal) init.signal = options.signal;
  if (options.body !== undefined) {
    init.body = isForm ? (options.body as FormData) : JSON.stringify(options.body);
  }

  try {
    return await fetch(url, init);
  } catch (error) {
    // AbortError — это не сбой сети, а осознанная отмена (ушли со страницы).
    // Превращать её в «нет связи» нельзя: TanStack Query покажет ошибку.
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError({ status: 0, message: HTTP_REASONS[0] ?? 'Нет связи', code: 'network' });
  }
}

/**
 * Тело ответа в значение.
 *
 * Пустое тело даёт null, а не undefined, и это важнее, чем кажется. Nest на
 * обработчике, вернувшем null, отправляет ответ БЕЗ тела — так отвечают все
 * маршруты вида «настройки, которых пока нет». А TanStack Query считает
 * undefined ошибкой запроса («data is undefined»), и вместо честного пустого
 * состояния экран показывал бы сбой.
 *
 * Для DELETE и logout, которым результат не нужен, null ничем не хуже.
 */
async function parse<T>(response: Response): Promise<T> {
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return null as T;
  }
  const text = await response.text();
  if (!text) return null as T;
  return JSON.parse(text) as T;
}

async function request<T>(method: Method, path: string, options: RequestOptions = {}): Promise<T> {
  let response = await rawRequest(method, path, options);

  if (response.status === 401 && !options.skipAuthRefresh) {
    const refreshed = await refreshSession();
    if (!refreshed) {
      // Сессии больше нет. Чистим состояние и уводим на вход ОДИН раз — а не
      // из каждого параллельного запроса, поймавшего тот же 401.
      handleSessionLost();
      throw await toApiError(response);
    }
    // Ровно один повтор: skipAuthRefresh отключает вторую попытку обновиться.
    response = await rawRequest(method, path, { ...options, skipAuthRefresh: true });
  }

  if (!response.ok) throw await toApiError(response);
  return parse<T>(response);
}

/** Типобезопасные методы. Генерик описывает ФОРМУ ОТВЕТА, не тела запроса. */
export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'body'>): Promise<T> =>
    request<T>('GET', path, options ?? {}),
  post: <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    request<T>('POST', path, { ...options, body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    request<T>('PATCH', path, { ...options, body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    request<T>('PUT', path, { ...options, body }),
  delete: <T = void>(path: string, options?: RequestOptions): Promise<T> =>
    request<T>('DELETE', path, options ?? {}),
  /** Загрузка файла: тело — FormData, Content-Type ставит браузер. */
  upload: <T>(path: string, form: FormData, options?: RequestOptions): Promise<T> =>
    request<T>('POST', path, { ...options, body: form }),
};

// ──────────────────────────── Сессия ────────────────────────────

/**
 * Один общий промис обновления на все параллельные 401.
 *
 * Ротация refresh-токена одноразовая: второй одновременный POST /auth/refresh
 * предъявит уже отозванный токен, и сервер справедливо посчитает это утечкой
 * (auth.refresh_reuse) и погасит всю сессию.
 */
let refreshInFlight: Promise<boolean> | null = null;

export function refreshSession(): Promise<boolean> {
  refreshInFlight ??= doRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

async function doRefresh(): Promise<boolean> {
  try {
    // В Mini App токен приезжает телом: cookie в webview ненадёжна.
    const stored = isMiniApp() ? await tgStorage.get(TG_REFRESH_KEY) : null;
    const body = stored ? { refreshToken: stored } : {};

    const auth = await request<AuthResponse>('POST', '/auth/refresh', {
      body,
      skipAuthRefresh: true,
    });

    await acceptAuth(auth);
    return true;
  } catch {
    return false;
  }
}

/** Разложить ответ входа/ротации: токен в память, refresh — в CloudStorage. */
export async function acceptAuth(auth: AuthResponse): Promise<AuthResponse> {
  setAccessToken(auth.accessToken, auth.expiresIn);
  if (auth.refreshToken) await tgStorage.set(TG_REFRESH_KEY, auth.refreshToken);
  return auth;
}

/**
 * Сессия кончилась: чистим всё и отдаём управление стору.
 *
 * Зовётся безусловно, а не «только если токен был в памяти»: на холодном
 * старте токена нет вовсе, и такая проверка молча проглатывала бы настоящий
 * разлогин. Решение, уводить ли оператора на /login, принимает обработчик —
 * он один знает, вошёл ли кто-нибудь вообще.
 */
function handleSessionLost(): void {
  setAccessToken(null);
  void tgStorage.remove(TG_REFRESH_KEY);
  onUnauthorized();
}

// ──────────────────────────── Маршруты auth ────────────────────────────

/**
 * Тонкие обёртки над /auth/*. Живут здесь, а не в сторе: они завязаны на
 * транспорт токена (cookie против CloudStorage), а стор — на состояние.
 */
export const authApi = {
  async login(email: string, password: string): Promise<AuthResponse> {
    const auth = await request<AuthResponse>('POST', '/auth/login', {
      body: { email, password },
      // 401 здесь — это неверный пароль, а не протухший токен: обновляться
      // нечем и незачем.
      skipAuthRefresh: true,
    });
    return acceptAuth(auth);
  },

  me(options?: { signal?: AbortSignal }): Promise<MeResponse> {
    return api.get<MeResponse>('/auth/me', options);
  },

  async logout(): Promise<void> {
    const stored = isMiniApp() ? await tgStorage.get(TG_REFRESH_KEY) : null;
    try {
      await request<void>('POST', '/auth/logout', {
        body: stored ? { refreshToken: stored } : {},
        skipAuthRefresh: true,
      });
    } catch {
      // Сервер недоступен — локальный выход всё равно обязан сработать.
    }
    setAccessToken(null);
    await tgStorage.remove(TG_REFRESH_KEY);
  },

  /** «Выйти на всех устройствах». */
  logoutAll(): Promise<{ revoked: number }> {
    return api.post<{ revoked: number }>('/auth/logout-all');
  },
};
